import { useAuth0 } from "@auth0/auth0-react";
import { useCallback, useState } from "react";

type MfaStepUpError = {
  error?: string;
  error_description?: string;
  mfa_token?: string;
};

type OtpTokenResponse = {
  access_token: string;
  id_token?: string;
  token_type?: string;
  expires_in?: number;
};

// When the cashier runs EMBEDDED in the host (production) it has no Auth0 session
// of its own (no refresh token), so the MFA step-up is performed server-side by the
// host at /api/auth/mfa-verify and the resulting token is returned. STANDALONE
// (dev/local, where the cashier owns its Auth0 session) keeps the in-browser flow.
const MFA_VERIFY_ROUTE = "/api/auth/mfa-verify";
const EMBEDDED_MFA_TOKEN_SENTINEL = "host-mint";

// VITE_AUTH0_DOMAIN is host-only in prod/staging (e.g. "pepperstone-dev.au.auth0.com"),
// but the local/CI mock override is a full origin with scheme (e.g. "http://mockoauth:8090").
// Accept both: use the value verbatim when it already carries a scheme, otherwise assume
// https. Normalize trailing slashes so we don't build URLs like "https://example.com//oauth/token".
const auth0Origin = (domain: string): string => {
  const origin = /^https?:\/\//i.test(domain) ? domain : `https://${domain}`;
  return origin.replace(/\/+$/, "");
};

// Mode is decided by the entry point, not by env vars. The standalone dev entry
// (index.tsx, outside qiankun) sets window.__CASHIER_STANDALONE__; the deployed MFE
// bundle (src/main.tsx) and every host embed never run it. So the marker's absence
// means embedded — true on staging and prod regardless of VITE_AUTH0_DOMAIN.
const isEmbedded = (): boolean => !window.__CASHIER_STANDALONE__;

const mapHostMfaError = (status: number, code?: string): string => {
  switch (status) {
    // 401 conflates a wrong OTP with an expired host session at the route.
    case 401:
      return "invalid_otp";
    case 403:
      return "mfa_origin_rejected";
    case 409:
      return code ?? "mfa_enrollment_required";
    case 440:
      return code ?? "session_reauth_required";
    default:
      return "mfa_unavailable";
  }
};

export interface UseWithdrawalOtpMfaReturn {
  mfaToken: string;
  isPending: boolean;
  error: Error | null;
  requestMfaChallenge: () => Promise<string>;
  verifyOtpCode: (otpCode: string) => Promise<string>;
}

export const useWithdrawalOtpMfa = (): UseWithdrawalOtpMfaReturn => {
  const auth0 = useAuth0();
  const [mfaToken, setMfaToken] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const requestMfaChallenge = useCallback(async () => {
    if (isEmbedded()) {
      // Host-mint: challenge + verify happen server-side, so there is no Auth0
      // mfa_token client-side. Set a sentinel so the UI "challenge ready" gate passes.
      setError(null);
      setMfaToken(EMBEDDED_MFA_TOKEN_SENTINEL);
      return EMBEDDED_MFA_TOKEN_SENTINEL;
    }

    setIsPending(true);
    setError(null);

    try {
      await auth0.getAccessTokenSilently({
        cacheMode: "off",
        authorizationParams: {
          custom_mfa: "true",
        },
      });

      throw new Error("MFA challenge was not required for this session");
    } catch (err) {
      const mfaError = err as MfaStepUpError;

      if (mfaError.error === "mfa_required" && mfaError.mfa_token) {
        setMfaToken(mfaError.mfa_token);
        return mfaError.mfa_token;
      }

      const fallbackError =
        err instanceof Error
          ? err
          : new Error("Failed to request MFA challenge");
      setError(fallbackError);
      throw fallbackError;
    } finally {
      setIsPending(false);
    }
  }, [auth0]);

  const verifyOtpCode = useCallback(
    async (otpCode: string) => {
      if (isEmbedded()) {
        if (!/^\d{6}$/.test(otpCode)) {
          const invalid = new Error("invalid_otp");
          setError(invalid);
          throw invalid;
        }

        setIsPending(true);
        setError(null);

        try {
          const response = await fetch(MFA_VERIFY_ROUTE, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            // Same-origin host route; the host __session cookie must ride along.
            credentials: "include",
            body: JSON.stringify({ otp: otpCode }),
          });

          const data = (await response.json().catch(() => ({}))) as {
            accessToken?: string;
            code?: string;
          };

          if (response.ok && data.accessToken) {
            return data.accessToken;
          }

          throw new Error(mapHostMfaError(response.status, data.code));
        } catch (err) {
          const fallbackError =
            err instanceof Error ? err : new Error("mfa_unavailable");
          setError(fallbackError);
          throw fallbackError;
        } finally {
          setIsPending(false);
        }
      }

      if (!mfaToken || !otpCode || otpCode.length < 6) {
        throw new Error("Invalid MFA token or OTP code");
      }

      setIsPending(true);
      setError(null);

      try {
        const auth0Domain = import.meta.env.VITE_AUTH0_DOMAIN;
        const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;

        const response = await fetch(
          `${auth0Origin(auth0Domain)}/oauth/token`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              client_id: clientId,
              mfa_token: mfaToken,
              otp: otpCode,
              grant_type: "http://auth0.com/oauth/grant-type/mfa-otp",
            }),
          }
        );

        const data = (await response.json()) as Partial<OtpTokenResponse> & {
          error?: string;
          error_description?: string;
        };

        if (!response.ok || !data.access_token) {
          throw new Error(
            data.error_description || data.error || "OTP verification failed"
          );
        }

        return data.access_token;
      } catch (err) {
        const fallbackError =
          err instanceof Error ? err : new Error("Failed to verify OTP code");
        setError(fallbackError);
        throw fallbackError;
      } finally {
        setIsPending(false);
      }
    },
    [mfaToken]
  );

  return {
    mfaToken,
    isPending,
    error,
    requestMfaChallenge,
    verifyOtpCode,
  };
};
